import {
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Snackbar,
  Divider,
  Box,
} from '@material-ui/core';
import * as React from 'react';
import { DelayedLinearProgress } from 'components';
import {
  ReservationSpecialDayService,
  ShopService,
  SettingsService,
  ReservationService,
} from 'services';
import { ReservationSpecialDay, OpeningHour, Setting, Shop } from 'interfaces';

/**
 * Slot-grid editor for per-date overrides. UX per user 2026-05-28:
 *
 *   1. Pick a date.
 *   2. See the standard slots for that day's weekday (from the To Go
 *      shop's opening_hours and the global slot-increment setting)
 *      laid out as a row of switches, ON by default.
 *   3. Toggle individual slots OFF to disable specific times.
 *      Toggle the "Closed all day" master switch to disable the entire day.
 *   4. Save. The availability endpoint reads disabled_slots and excludes
 *      them when telling the website which times the customer can pick.
 *
 * INLINE, no Dialog. This UI lives directly in the page; clicking around
 * doesn't open/close a modal, so the periodic-remount bug we saw with
 * FormDialog doesn't apply here.
 */

const TO_GO_SHOP_ID = 2; // matches App\Lib\ShopTypes::TO_GO

const toIsoDate = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const dayOfWeekIso = (dateStr: string) => {
  // Mon=1 ... Sun=7
  const d = new Date(dateStr + 'T00:00:00');
  let dow = d.getDay();
  return dow === 0 ? 7 : dow;
};

const parseSlots = (csv: string | null | undefined): string[] => {
  if (!csv) return [];
  return csv
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
};

const computeSlots = (
  fromTime: string,
  toTime: string,
  incrementMinutes: number,
): string[] => {
  if (!fromTime || !toTime) return [];
  const slots: string[] = [];
  const [fh, fm] = fromTime.split(':').map((s) => parseInt(s, 10));
  const [th, tm] = toTime.split(':').map((s) => parseInt(s, 10));
  let cur = fh * 60 + fm;
  let end = th * 60 + tm;
  if (end <= cur) end += 24 * 60;
  while (cur < end) {
    const h = Math.floor((cur % (24 * 60)) / 60);
    const m = cur % 60;
    slots.push(
      `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`,
    );
    cur += incrementMinutes;
  }
  return slots;
};

export const SpecialDaysTab: React.FC = () => {
  const [date, setDate] = React.useState<string>(toIsoDate(new Date()));
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState<string | null>(null);

  // Resolved state for the selected date
  const [existing, setExisting] = React.useState<ReservationSpecialDay | null>(
    null,
  );
  const [allSlots, setAllSlots] = React.useState<string[]>([]);
  const [disabledSet, setDisabledSet] = React.useState<Set<string>>(new Set());
  const [closedAllDay, setClosedAllDay] = React.useState(false);
  const [note, setNote] = React.useState('');
  const [hoursMessage, setHoursMessage] = React.useState<string | null>(null);

  // Shared once-loaded data
  const [openingHours, setOpeningHours] = React.useState<OpeningHour[]>([]);
  const [increment, setIncrement] = React.useState(30);

  React.useEffect(() => {
    Promise.all([ShopService.getShops(), SettingsService.getSettings()])
      .then(([shops, settings]) => {
        const shopList = (shops as Shop[]) || [];
        const toGo = shopList.find((s) => s.id === TO_GO_SHOP_ID);
        setOpeningHours(toGo?.openingHours || []);
        const incRow = (settings as Setting[]).find(
          (s) => s.key === 'reservation_time_slot_increment_minutes',
        );
        if (incRow) setIncrement(parseInt(incRow.value, 10) || 30);
      })
      .catch(() => {
        setOpeningHours([]);
      });
  }, []);

  const loadDate = React.useCallback(() => {
    if (!date) return;
    setLoading(true);
    setHoursMessage(null);
    ReservationSpecialDayService.getAll()
      .then((all) => {
        const row =
          (all || []).find((d) => d.specialDate.slice(0, 10) === date) ||
          null;
        setExisting(row);
        setClosedAllDay(!!row?.closedAllDay);
        setNote(row?.note || '');
        setDisabledSet(new Set(parseSlots(row?.disabledSlots)));
      })
      .catch(() => setExisting(null))
      .finally(() => setLoading(false));
  }, [date]);

  React.useEffect(() => {
    loadDate();
  }, [loadDate]);

  // Recompute slot list whenever the date, opening hours, or special-day
  // overrides change.
  React.useEffect(() => {
    if (!date) {
      setAllSlots([]);
      return;
    }
    let fromTime: string | null = null;
    let toTime: string | null = null;

    // Special-day override of opening hours, if set
    if (
      existing &&
      existing.fromTime &&
      existing.toTime &&
      !existing.closedAllDay
    ) {
      fromTime = (existing.fromTime || '').slice(0, 5);
      toTime = (existing.toTime || '').slice(0, 5);
    } else {
      const dow = dayOfWeekIso(date);
      const oh = openingHours.find(
        (h) => (h as any).dayOfWeek === dow,
      );
      if (oh && oh.fromTime && oh.toTime) {
        fromTime = oh.fromTime.slice(0, 5);
        toTime = oh.toTime.slice(0, 5);
      } else {
        setHoursMessage(
          'No opening hours set for this weekday. Configure them under Reservations > Settings > Opening Hours.',
        );
      }
    }

    if (fromTime && toTime) {
      setAllSlots(computeSlots(fromTime, toTime, increment));
    } else {
      setAllSlots([]);
    }
  }, [date, openingHours, increment, existing]);

  const toggleSlot = (slot: string) => {
    setDisabledSet((prev) => {
      const next = new Set(prev);
      if (next.has(slot)) next.delete(slot);
      else next.add(slot);
      return next;
    });
  };

  const setAll = (disable: boolean) => {
    setDisabledSet(disable ? new Set(allSlots) : new Set());
  };

  const handleSave = async () => {
    setSaving(true);
    const disabledArr = allSlots.filter((s) => disabledSet.has(s));
    const payload = {
      special_date: date,
      closed_all_day: closedAllDay ? 1 : 0,
      disabled_slots: disabledArr.length ? disabledArr.join(',') : null,
      note: note || null,
    };
    try {
      if (existing) {
        await ReservationSpecialDayService.update(existing.id, payload);
      } else {
        // Only create a row if there's actually an override to persist.
        if (closedAllDay || disabledArr.length || note) {
          await ReservationSpecialDayService.create(payload);
        }
      }
      setSnackbar('Saved');
      loadDate();
    } catch (e) {
      setSnackbar((e as Error)?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!existing) return;
    setSaving(true);
    try {
      await ReservationSpecialDayService.remove(existing.id);
      setSnackbar('Override removed; this date follows the regular schedule.');
      loadDate();
    } catch (e) {
      setSnackbar((e as Error)?.message || 'Failed to clear');
    } finally {
      setSaving(false);
    }
  };

  return (
    <React.Fragment>
      <Paper style={{ padding: 16, marginBottom: 16 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              label="Date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={closedAllDay}
                  onChange={(_, v) => setClosedAllDay(v)}
                />
              }
              label="Closed all day"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Note (admin reason, optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
        </Grid>
      </Paper>

      {loading && <DelayedLinearProgress />}

      <Paper style={{ padding: 16 }}>
        {hoursMessage && (
          <Typography variant="body2" color="error" style={{ marginBottom: 8 }}>
            {hoursMessage}
          </Typography>
        )}

        {allSlots.length === 0 && !hoursMessage && (
          <Typography variant="body2" color="textSecondary">
            No slots available for this date. Either the weekday has no
            opening hours, or the time-slot increment setting is empty.
          </Typography>
        )}

        {allSlots.length > 0 && (
          <React.Fragment>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              style={{ marginBottom: 12 }}
            >
              <Typography variant="subtitle2">
                Time slots — toggle OFF to disable specific times.{' '}
                {closedAllDay && (
                  <strong>(Closed all day is on; slots are ignored)</strong>
                )}
              </Typography>
              <Box>
                <Button size="small" onClick={() => setAll(false)} disabled={closedAllDay}>
                  Enable all
                </Button>
                <Button size="small" onClick={() => setAll(true)} disabled={closedAllDay}>
                  Disable all
                </Button>
              </Box>
            </Box>
            <Divider style={{ marginBottom: 12 }} />
            <Grid container spacing={1}>
              {allSlots.map((slot) => {
                const enabled = !disabledSet.has(slot);
                return (
                  <Grid item xs={6} sm={4} md={3} lg={2} key={slot}>
                    <Paper
                      variant="outlined"
                      style={{
                        padding: '6px 12px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        background:
                          closedAllDay || !enabled ? '#ffebee' : '#e8f5e9',
                        opacity: closedAllDay ? 0.5 : 1,
                      }}
                    >
                      <span style={{ fontWeight: 600 }}>{slot}</span>
                      <Switch
                        size="small"
                        checked={enabled}
                        disabled={closedAllDay}
                        onChange={() => toggleSlot(slot)}
                      />
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </React.Fragment>
        )}

        <Box display="flex" justifyContent="flex-end" style={{ marginTop: 16, gap: 8 }}>
          {existing && (
            <Button color="default" onClick={handleClear} disabled={saving}>
              Remove override
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
          >
            Save
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
      />
    </React.Fragment>
  );
};
